/* ============================================================
   Fence Works — build-sequence data (crew build sheet)

   The ordered install sequence per fence kind, duplicated ON
   PURPOSE from src/fencebuild.tsx's build definitions so the
   crew sheet (and anything else) can read the steps WITHOUT
   touching the 3D viewer — this module is pure data, zero
   three.js, importable anywhere. Keep titles/specs in lockstep
   with fencebuild's step `title`/`sub`/`cap`/`qk` — that viewer
   is the source of truth; a later refactor can make it consume
   this table too.

   buildStepsFor(spec) — spec is the same shape fenceSpecFrom
   (quote.tsx) produces: { category, style, color, heightFt,
   panelFt } plus optional crew-sheet context:
     boardIn     6 | 11.33  — vinyl board width (default 6)
     kickboard   boolean    — wood kickboard variant line
     gates       number     — explicit gate-install steps
                              included when > 0 (default 1, like
                              the viewer's generic 3-bay + gate
                              run): vinyl/wood set dedicated gate
                              posts, mount hinges, hang the leaf,
                              install the latch, then adjust swing
                              & catch; chain/alum hang the welded
                              leaf, install the latch, then adjust
     jchannel    boolean    — "Install U-channel post trim" step
                              (premium add-on, VINYL ONLY)
     surveyRuns  [{ name, pct }] — measured-grade runs; when
                              present, "Shoot grade" is inserted
                              right after "Mark property", same
                              as the viewer's survey step
   ============================================================ */

export type BuildStep = {
  n: number;        // 1-based step number
  title: string;    // step name ("Drive steel posts")
  spec: string;     // the spec line (fencebuild's `sub`)
  note?: string;    // plain-English line (fencebuild's `cap`)
  qk?: string[];    // quantity keys, first match against BOM class counts
  qd?: number;      // divide the matched count (vinyl rails split top/bottom)
};

/* app categories → one of the four build kinds (mirror of fencebuild kindOf) */
export function buildKindOf(category: any) {
  const c = String(category || "").toLowerCase();
  if (/chain/.test(c)) return "chain";
  if (/alum|iron|ornament/.test(c)) return "alum";
  if (/wood|cedar|farm|ranch/.test(c)) return "wood";
  return "vinyl"; // vinyl, composite, unknown → the flagship build
}

const fmtPct = (v: any) => (Math.round(v * 10) / 10).toFixed(Math.round(v * 10) % 10 ? 1 : 0);
/* feet -> how a contractor says it ("4'6\"", "42\"") */
const bsFtIn = (v: any) => {
  const n = Math.round((+v || 0) * 12), ft = Math.floor(n / 12), i = n % 12;
  return i ? (ft ? `${ft}'${i}"` : `${i}"`) : `${ft}'`;
};

const MARK_STEP = {
  title: "Mark property", spec: "survey stakes · string line · marking paint",
  note: "We stake the corners and snap a string line so every post lands exactly where it should.",
};

/* "Shoot grade" — only on jobs with measured elevation profiles */
function surveyStepFor(runs: any[]) {
  const pr = (Array.isArray(runs) ? runs : []).filter((r: any) => r && isFinite(+r.pct));
  return {
    title: "Shoot grade",
    spec: "laser level · stadia shots" + (pr.length ? " · " + pr.map((r: any) => `${String(r.name || "").toUpperCase() || "RUN"} RUN · ${fmtPct(+r.pct)}% GRADE`).join(" · ") : ""),
    note: "We shoot each run with a laser level and mark the real drops — your fence is built to the actual grade, not a guess.",
  };
}

/* ---- Vinyl Privacy: the owner's exact steel-post + sleeve method ---- */
function vinylSteps(s: any) {
  const boardIn = +s.boardIn === 11.33 ? 11.33 : 6;
  const embedIn = Math.round(+s.embedIn || 42);
  const exposed = bsFtIn(Math.max(1, Math.min((+s.pipeFt || 8) - embedIn / 12, s.heightFt - 0.45)));
  // the product's post-setting method, same as the 3D build: the crew sheet
  // must not hand the crew the opposite install from the saved configuration
  if (s.postSet === "concrete") return [
    {
      title: "Footings", spec: `12" Ø × ${embedIn}" concrete · 4000 PSI`, qk: ["footings"],
      note: `Every post gets a ${embedIn}" concrete footing below the frost line so the fence never heaves or leans.`,
    },
    {
      title: "Set vinyl posts", spec: `5"×5" vinyl · set ${embedIn}" in wet concrete · plumbed · routed slots top & bottom`, qk: ["sleeves"],
      note: "The vinyl post goes straight into wet concrete and gets plumbed before it cures — no steel inside it, so the footing is what holds the line.",
    },
    ...vinylBodySteps(boardIn),
  ];
  return [
    {
      title: "Drive steel posts", spec: `2-3/8" galvanized pipe · ${+s.pipeFt || 8}' · driven ${embedIn}" · ${exposed} above grade · no concrete`, qk: ["posts"],
      note: `Steel pipe is driven ${embedIn}" into undisturbed soil — well past the frost line, stronger than a concrete-set post, with nothing to cure. That leaves ${exposed} of steel above grade for the sleeve to lock onto.`,
    },
    {
      title: "Set post collars", spec: `two-ring collars · 2 per post · bottom 12-16" above grade · top at the pipe top (${exposed}) · self-tapping screws`, qk: ["fittings"],
      note: "Two-ring collars (a small ring inside a large ring) screw onto the steel — one low, one at the pipe top — and lock each sleeve to height. Bottom collars run parallel to the fence, the tops sit perpendicular, and a string line on the tops sets the whole run dead straight before the sleeves drop on.",
    },
    {
      title: "Vinyl post sleeves", spec: `5"×5" vinyl · slides over ${exposed} of steel · routed slots top & bottom`, qk: ["sleeves"],
      note: "Heavy-wall vinyl posts slide over the steel — the steel does the work, the vinyl never rots or peels.",
    },
    ...vinylBodySteps(boardIn),
  ];
}

/* everything after the posts — the same fence whichever way the posts went in */
function vinylBodySteps(boardIn: number) {
  return [
    {
      title: "Bottom rail", spec: `1-1/2" × 5-1/2" pocket rail · galvanized channel reinforcement · crimped ends · slides into bottom slot`, qk: ["rails"], qd: 2,
      note: "The bottom rail locks into routed pockets in each post — and a galvanized channel slips inside it so the span that takes the most load can never sag or work loose.",
    },
    {
      title: "Boards", spec: `tongue & groove · ${boardIn === 6 ? '6"' : '11-1/3"'} wide · slide into the rail pocket`, qk: ["boards"],
      note: "Tongue-and-groove boards drop in one at a time and lock together into a solid privacy wall.",
    },
    {
      title: "Top rail", spec: `1-1/2" × 5-1/2" cap rail · crimped ends · slides into top slot`, qk: ["rails"], qd: 2,
      note: "The top rail caps the boards and ties every panel into one rigid frame.",
    },
    {
      title: "Lock screws", spec: "through post wall into rail ends · prevents rail slide-out · 2/end", qk: ["screws"],
      note: "A screw through each rail end means panels can never lift or rattle out in the wind.",
    },
    {
      title: "Caps", spec: `5"×5" flush vinyl post cap`, qk: ["caps"],
      note: "Caps seal water out of every post and give the line its clean finished top.",
    },
  ];
}
/* explicit gate-install sequence — mirror of fencebuild's split gate steps.
   Vinyl/wood set dedicated gate posts first (the privacy gate hangs on its own
   posts, built at this step); chain/aluminum gate posts are structural
   terminals already standing, so those start at "Hang gate". */
const VINYL_GATE = [
  {
    title: "Mount hinges", spec: "self-mortised hinges bolted to the hinge post · leaf side knuckles", qk: ["gates"],
    note: "Heavy hinges mortise into the hinge post first — squared and shimmed so the leaf will hang dead-level.",
  },
  {
    title: "Hang the gate leaf", spec: "matching panel · diagonal brace · set onto the hinges", qk: ["gates"],
    note: "The gate leaf is built from the same panel parts, braced against sag, and lifted onto the waiting hinges.",
  },
  {
    title: "Install latch", spec: "gate latch mounted to the leaf · drops onto the latch-post keeper", qk: ["gates"],
    note: "The latch mounts on the swing edge so the gate closes with a positive click.",
  },
  {
    title: "Adjust swing & catch", spec: "hinge tension set · strike keeper on the latch post · swings free & catches", qk: ["gates"],
    note: "We set the hinge tension and the strike keeper so it swings free, self-centers, and latches every time.",
  },
];

/* ---- Wood privacy (dog-ear) + Board-on-Board / Shadow Box / Horizontal ---- */
function woodSteps(s: any) {
  const H = s.heightFt;
  const style = String(s.style || "");
  const variant = /board on board/i.test(style) ? "bob" : /shadow/i.test(style) ? "shadow" : /horiz/i.test(style) ? "horiz" : "dogear";
  const railCount = H >= 6 ? 3 : 2;
  const kick = !!s.kickboard;
  return [
    ...(s.postSet === "driven" ? [] : [{
      title: "Footings", spec: `12" Ø × ${Math.round(+s.embedIn || 24)}" concrete · 4000 PSI`, qk: ["footings"],
      note: "Every post gets a concrete footing below the frost line so the fence never heaves or leans.",
    }]),
    {
      title: "Set posts",
      spec: s.postSet === "driven"
        ? `4×4 pressure-treated · driven ${Math.round(+s.embedIn || 42)}" · plumbed`
        : "4×4 pressure-treated · set in wet concrete · plumbed",
      qk: ["posts"],
      note: "Pressure-treated posts go into wet concrete and get plumbed with a level before it cures.",
    },
    {
      title: "Rails", spec: variant === "horiz" ? "2×4 mid-span nailer · horizontal style" : `2×4 rails · ${railCount} per bay · face-mounted`, qk: ["rails"],
      note: variant === "horiz" ? "A mid-span nailer gives the long horizontal boards solid backing." : "2×4 rails bridge post to post — the skeleton every picket hangs on.",
    },
    {
      title: "Boards", qk: ["boards"],
      spec: variant === "horiz"
        ? `1×6 boards · horizontal · ${kick ? "2×6 kickboard first · " : ""}stacked`
        : `1×6 ${variant === "dogear" ? "dog-ear" : variant === "bob" ? "overlapped double-layer" : "alternating-face"} pickets · ${kick ? "2×6 kickboard first · " : ""}nailed sequentially`,
      note: variant === "bob" ? "Overlapping layers leave zero gaps — full privacy even after the wood seasons."
        : variant === "shadow" ? "Boards alternate front and back — finished from both sides, and the breeze still gets through."
        : variant === "horiz" ? "Boards stack in clean level lines, each one fastened tight to the frame."
        : "Pickets go up one at a time, snugged tight to a string line and nailed top and bottom.",
    },
    {
      title: "Top cap & trim", spec: "2×6 flat cap · 1×4 trim band", qk: ["caps"],
      note: "A flat cap and trim band finish the top and shed water off the end grain.",
    },
    {
      title: "Secure", spec: "exterior screws · 2 per rail-post joint", qk: ["screws"],
      note: "Exterior-grade screws at every rail joint grip for decades and never rust-streak the wood.",
    },
  ];
}
const WOOD_GATE = [
  {
    title: "Mount hinges", spec: "self-mortised hinges bolted to the hinge post · leaf side knuckles", qk: ["gates"],
    note: "Heavy hinges mortise into the hinge post first — squared and shimmed so the leaf will hang dead-level.",
  },
  {
    title: "Hang the gate leaf", spec: "braced picket frame · anti-sag diagonal · set onto the hinges", qk: ["gates"],
    note: "The braced picket leaf — with its anti-sag diagonal — is lifted onto the waiting hinges so it swings true for years.",
  },
  {
    title: "Install latch", spec: "gate latch mounted to the leaf · drops onto the latch-post keeper", qk: ["gates"],
    note: "The latch mounts on the swing edge so the gate closes with a positive click.",
  },
  {
    title: "Adjust swing & catch", spec: "hinge tension set · strike keeper on the latch post · swings free & catches", qk: ["gates"],
    note: "We set the hinge tension and the strike keeper so it swings free, self-centers, and latches every time.",
  },
];

/* ---- Chain link: posts → rail → hardware → stretched fabric → ties ---- */
function chainSteps(s: any) {
  return [
    {
      title: "Set posts", spec: `2-3/8" terminal + 1-7/8" line posts · ${s.postSet === "driven" ? `driven ${Math.round(+s.embedIn || 42)}"` : "set in concrete · crowned"}`, qk: ["posts"],
      note: "Heavy terminal posts anchor the ends and corners; lighter line posts carry the run between.",
    },
    {
      title: "Top rail", spec: `1-3/8" rail · through loop caps · terminals get dome caps`, qk: ["rails"],
      note: "The top rail ties every post together and keeps the fabric height dead even.",
    },
    {
      title: "Tension bands", spec: "bands + brace hardware on every terminal post", qk: ["fittings"],
      note: "Bands on each terminal post grip the stretcher bar that holds the fabric tight.",
    },
    {
      title: "Stretch fabric", spec: `2" mesh · 9 ga galvanized · stretched terminal to terminal`, qk: ["fabric", "boards"],
      note: "The mesh gets stretched drum-tight from end to end — no sag, no rattle.",
    },
    {
      title: "Tie wires", spec: `aluminum ties · every 12" along the top rail`, qk: ["ties"],
      note: "Ties clip the mesh to the rail about every foot so it stays smooth and quiet.",
    },
  ];
}
const CHAIN_GATE = [
  {
    title: "Hang gate", spec: "welded pipe frame · diagonal truss · swings on the gate post", qk: ["gates"],
    note: "A welded-frame gate hangs level off the gate post so it never racks out of square.",
  },
  {
    title: "Install latch", spec: "fork latch on the gate post · drops over the leaf rail", qk: ["gates"],
    note: "The latch mounts on the swing edge so the gate closes with a positive click.",
  },
  {
    title: "Adjust swing & catch", spec: "hinge swing set · fork-latch keeper · swings free & catches", qk: ["gates"],
    note: "We set the hinge tension and the strike keeper so it swings free, self-centers, and latches every time.",
  },
];

/* ---- Aluminum / ornamental: pre-welded panels on square posts ---- */
function alumSteps(s: any) {
  const rails3 = /3/.test(String(s.style || "")) || s.heightFt >= 5;
  const railCount = rails3 ? 3 : 2;
  return [
    {
      title: "Set posts", spec: `2"×2" aluminum posts · ${s.postSet === "driven" ? `driven ${Math.round(+s.embedIn || 42)}"` : "set in concrete"} · spaced to panel`, qk: ["posts"],
      note: "Posts get set in concrete at exact panel spacing — the welded panels demand it.",
    },
    {
      title: "Hang panels", spec: `pre-welded panels · 5/8" pickets · ${railCount} rails · racked to grade`, qk: ["boards"],
      note: "Factory-welded panels rack to follow your grade and bolt between the posts.",
    },
    {
      title: "Self-tap screws", spec: "panel brackets screwed to posts · 2 per rail end", qk: ["screws"],
      note: "Self-tapping screws lock each bracket home — hidden from the street side.",
    },
    {
      title: "Ball caps", spec: "die-cast ball caps on every post", qk: ["caps"],
      note: "A die-cast cap dresses the top of every post.",
    },
  ];
}
const ALUM_GATE = [
  {
    title: "Hang gate", spec: "welded gate · self-closing hinges · racked to grade", qk: ["gates"],
    note: "The welded gate hangs on self-closing hinges so it always swings shut behind you.",
  },
  {
    title: "Install latch", spec: "gravity latch on the gate · pool-code friendly · self-latching", qk: ["gates"],
    note: "The latch mounts on the swing edge so the gate closes with a positive click.",
  },
  {
    title: "Adjust swing & catch", spec: "self-closing hinge tension set · latch keeper · swings shut & catches", qk: ["gates"],
    note: "We set the hinge tension and the strike keeper so it swings free, self-centers, and latches every time.",
  },
];

/* U-channel post trim — premium vinyl add-on (mirror of the viewer's step) */
const JCHANNEL_STEP = {
  title: "Install U-channel post trim", spec: "slim vinyl U-channel down each post · clean finished panel edges", qk: ["jchannel"],
  note: "A slim U-channel snaps over each post edge where the panel meets it — a crisp, finished line with no exposed cut ends.",
};

/* the ordered build sequence for a fence spec — mirror of fencebuild makeDef:
   kind steps, survey spliced in right after "Mark property" when measured
   profiles exist, Gate appended when the job has gates */
export function buildStepsFor(spec: any): BuildStep[] {
  const s = spec || {};
  const kind0 = buildKindOf(s.category);
  const postSet = s.postSet === "concrete" || s.postSet === "driven" ? s.postSet : (kind0 === "vinyl" ? "driven" : "concrete");
  const norm = {
    ...s,
    heightFt: Math.max(3, Math.min(8, +s.heightFt || 6)),
    panelFt: Math.max(4, Math.min(12, +s.panelFt || 8)),
    // the crew sheet quotes the SAME depths the 3D build shows the customer
    postSet,
    embedIn: Math.max(6, Math.min(84, +s.embedIn || (postSet === "driven" ? 42 : 24))),
    pipeFt: Math.max(3, Math.min(14, +s.pipeFt || 8)),
  };
  const kind = buildKindOf(norm.category);
  const body = kind === "wood" ? woodSteps(norm) : kind === "chain" ? chainSteps(norm) : kind === "alum" ? alumSteps(norm) : vinylSteps(norm);
  const gate = kind === "wood" ? WOOD_GATE : kind === "chain" ? CHAIN_GATE : kind === "alum" ? ALUM_GATE : VINYL_GATE;
  const steps: any[] = [{ ...MARK_STEP }, ...body];
  const surveyRuns = Array.isArray(s.surveyRuns) ? s.surveyRuns.filter((r: any) => r && isFinite(+r.pct)) : [];
  if (surveyRuns.length) steps.splice(1, 0, surveyStepFor(surveyRuns)); // right after "Mark property"
  // U-channel post trim (premium add-on) — vinyl only. It's structural: the
  // channel goes on after the bottom rail and before the planks (install guide
  // step 3), so splice it in just before the "Boards" step. Off unless the flag is on.
  if (kind === "vinyl" && s.jchannel) {
    const bi = steps.findIndex((st: any) => /board|plank/i.test(st.title || ""));
    steps.splice(bi >= 0 ? bi : steps.length, 0, { ...JCHANNEL_STEP });
  }
  const gates = s.gates == null ? 1 : Math.max(0, s.gates | 0); // default mirrors the generic 3-bay + gate run
  if (gates > 0) gate.forEach((gs: any) => steps.push({ ...gs })); // explicit gate-install sequence
  return steps.map((st: any, i: number) => ({ n: i + 1, ...st }));
}

/* BOM item label → quantity class — mirror of fencebuild bomClass (order
   matters: specific words before generic "post" so "Post cap" lands on Caps) */
export function bomClassOf(item: any) {
  const t = String(item || "").toLowerCase();
  if (/post kit/.test(t)) return "posts";          // legacy per-post kit
  if (/u.?channel/.test(t)) return "jchannel";     // before "post" — "U-channel post trim"
  if (/gate/.test(t)) return "gates";
  if (/cap/.test(t)) return "caps";
  if (/sleeve/.test(t)) return "sleeves";
  if (/\btie/.test(t)) return "ties";
  if (/fitting|bracket|band|tension|hardware|hinge|latch/.test(t)) return "fittings";
  if (/concrete|cement|footing|\bbag/.test(t)) return "footings";
  if (/rail/.test(t)) return "rails";
  if (/fabric|mesh/.test(t)) return "fabric";
  if (/board|picket|panel|slat|kick/.test(t)) return "boards";
  if (/screw|nail|fastener/.test(t)) return "screws";
  if (/post|pipe|upright/.test(t)) return "posts";
  return null;
}

/* BOM rows [{ item, qty }] → per-class totals (the viewer's bomCounts);
   `extra` (e.g. { posts, gates } from the engine) overlays on top so step
   counts always agree with what the invoice bills */
export function bomStepCounts(bomRows: any, extra?: any) {
  const cc: any = {};
  (Array.isArray(bomRows) ? bomRows : []).forEach((r: any) => {
    const q = Math.round(+(r && r.qty != null ? r.qty : 0));
    if (!(q > 0)) return;
    const k = bomClassOf(r && r.item);
    if (k) cc[k] = (cc[k] || 0) + q;
  });
  if (extra && typeof extra === "object") Object.keys(extra).forEach((k) => { if (isFinite(+extra[k]) && +extra[k] > 0) cc[k] = Math.round(+extra[k]); });
  return cc;
}

/* quantity badge for one step (fencebuild's qk/qd lookup): the first quantity
   class the step's qk matches, divided by qd — null when nothing matches */
export function stepQtyFor(step: any, counts: any) {
  if (!step || !step.qk || !counts) return null;
  for (const k of step.qk) if (counts[k] != null) return Math.max(1, Math.round(counts[k] / (step.qd || 1)));
  return null;
}
